import {
  createSlice,
  createEntityAdapter,
  createSelector,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import { HYDRATE } from "next-redux-wrapper";
import { camelizeKeys } from "humps";
import { createWrapper } from "admin/api/axiosWrapper";
import { ErrorResponse } from "admin/types/errorResponse";
import {
  isFulfilledAction,
  isPendingAction,
  isRejectedAction,
} from "admin/utils/sliceUtil";
import { Newsletter } from "admin/types/newsletter";
import { RootState } from "admin/store";
import {
  API_BASE_PATH,
  COMPANY_ADMIN_API_VER,
} from "admin/constants/constants";
import { PageContext } from "admin/types/pageContext";
import { AdminTypes } from "admin/constants/adminTypes";
import { Meta } from "admin/types/meta";
import { isObjectEmpty, removeEmpty } from "admin/utils/objectUtil";

const key = "newsletter";
const adapter = createEntityAdapter<Newsletter.Newsletter, number>({
  selectId: (newsletter) => newsletter.id,
});

const initialStateValues: Newsletter.NewsletterState = {
  newsletter: null,
  recipientCount: null,
  errors: null,
  loading: false,
  meta: null,
};

const initialState = adapter.getInitialState(initialStateValues);

/** Async **/
export const fetchNewsletters = createAsyncThunk<
  {
    newsletters: Newsletter.Newsletter[];
    meta: Meta;
  },
  {
    context: PageContext.PageContext;
    param?: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchNewsletters`, async (args, { rejectWithValue }) => {
  try {
    const { context, param, isServerSide = false, headers, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/email_newsletters.json?${param}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/email_newsletters.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      newsletters: result.data.emailNewsletters,
      meta: result.data.meta,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchNewsletter = createAsyncThunk<
  {
    newsletter: Newsletter.Newsletter;
  },
  {
    context: PageContext.PageContext;
    id: number;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchNewsletter`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, isServerSide = false, headers, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/email_newsletters/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/email_newsletters/${id}.json`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      newsletter: result.data.emailNewsletter,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const postNewsletter = createAsyncThunk<
  { newsletter: Newsletter.Newsletter },
  { context: PageContext.PageContext; params: any },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/postNewsletter`, async (args, { rejectWithValue }) => {
  try {
    const { context, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/email_newsletters.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/email_newsletters.json`;
    const result = await createWrapper().post(url, {
      email_newsletter: params,
    });
    return { newsletter: result.data.emailNewsletter };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const putNewsletter = createAsyncThunk<
  { newsletter: Newsletter.Newsletter },
  {
    context: PageContext.PageContext;
    id: number;
    params: any;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/putNewsletter`, async (args, { rejectWithValue }) => {
  try {
    const { context, id, params } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/email_newsletters/${id}.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/email_newsletters/${id}.json`;
    const result = await createWrapper().put(url, { email_newsletter: params });
    return { newsletter: result.data.emailNewsletter };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const fetchRecipientCount = createAsyncThunk<
  {
    count: number;
  },
  {
    context: PageContext.PageContext;
    param?: string;
    isServerSide?: boolean;
    headers?: any;
    locale?: string;
  },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/fetchRecipientCount`, async (args, { rejectWithValue }) => {
  try {
    const { context, param, isServerSide = false, headers, locale } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/email_newsletter_recipients.json?${param}`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/email_newsletter_recipients.json?${param}`;
    const result = await createWrapper({ isServerSide, locale }).get(url, {
      headers,
    });
    return {
      count: result.data.recipientsCount,
    };
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const sendNewsletter = createAsyncThunk<
  void,
  { context: PageContext.PageContext; id: number },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/sendNewsletter`, async (args, { rejectWithValue }) => {
  try {
    const { context, id } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/email_newsletters/${id}/bulk_sending.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/email_newsletters/${id}/bulk_sending.json`;
    await createWrapper().post(url);
    return;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

export const sendTestNewsletter = createAsyncThunk<
  void,
  { context: PageContext.PageContext; id: number },
  {
    rejectValue: ErrorResponse;
  }
>(`${key}/sendTestNewsletter`, async (args, { rejectWithValue }) => {
  try {
    const { context, id } = args;
    const url =
      context.adminType === AdminTypes.manager
        ? `${API_BASE_PATH}/manager/companies/${context.companyCode}/email_newsletters/${id}/test_sending.json`
        : `${API_BASE_PATH}/company_admin/${COMPANY_ADMIN_API_VER}/email_newsletters/${id}/test_sending.json`;
    await createWrapper().post(url);
    return;
  } catch (err) {
    return rejectWithValue(err.data.errors);
  }
});

/** slice **/
export const newsletterSlice = createSlice({
  name: key,
  initialState,
  reducers: {
    setNewsletter: (state, action) => {
      state.newsletter = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // ページ間でメールコンテンツデータを共有するためHYDRATEしない
      // もしページ単位でサーバーにPOSTするならHYDRATEしてSSRでメールデータを取得する
      .addCase(HYDRATE, (state, action: any) => {
        const data = action.payload.newsletter;
        const newsletter =
          state.newsletter && !isObjectEmpty(state.newsletter)
            ? state.newsletter
            : data.newsletter;
        return { ...state, ...action.payload.newsletter, newsletter };
      })
      .addCase(fetchNewsletters.fulfilled, (state, action) => {
        adapter.setAll(state, action.payload.newsletters);
        state.meta = action.payload.meta;
      })
      .addCase(fetchNewsletter.fulfilled, (state, action) => {
        state.newsletter = action.payload.newsletter;
      })
      .addCase(postNewsletter.fulfilled, (state, action) => {
        state.newsletter = action.payload.newsletter;
      })
      .addCase(putNewsletter.fulfilled, (state, action) => {
        state.newsletter = action.payload.newsletter;
      })
      .addCase(fetchRecipientCount.fulfilled, (state, action) => {
        state.recipientCount = action.payload.count;
      })
      // 一旦slice単位で共通化
      .addMatcher(isPendingAction(key), (state) => {
        state.loading = true;
        state.errors = null;
      })
      .addMatcher(isFulfilledAction(key), (state) => {
        state.loading = false;
      })
      .addMatcher(isRejectedAction(key), (state, action: any) => {
        state.errors = action.payload;
        state.loading = false;
      });
  },
});

/** selector **/
export const {
  selectById: newsletterByIdSelector,
  selectIds: newsletterIdsSelector,
  selectEntities: newsletterEntitiesSelector,
  selectAll: newslettersSelector,
  selectTotal: totalNewslettersSelector,
} = adapter.getSelectors((state: RootState) => state.newsletter);

const stateSelector = (state: RootState) => state[key];

export const newsletterSelector = createSelector(
  stateSelector,
  (state) => state.newsletter,
);

export const errorsSelector = createSelector(
  stateSelector,
  (state) => state.errors,
);

export const loadingSelector = createSelector(
  stateSelector,
  (state) => state.loading,
);

export const newslettersMetaSelector = createSelector(
  stateSelector,
  (state) => state.meta,
);

export const recipientCountSelector = createSelector(
  stateSelector,
  (state) => state.recipientCount,
);

/** action export **/
export const { setNewsletter } = newsletterSlice.actions;

export const convertNewsletterFormValuesToNewsletter = (data: any) => {
  const newsletter: Newsletter.Newsletter = { ...camelizeKeys(data) };
  return newsletter;
};

export const convertNewsletterFormValuesToParams = (data: any) => {
  const params = { ...data };
  if (params.recipient_filter) {
    params.filter_json = JSON.stringify(removeEmpty(params.recipient_filter));
    delete params.recipient_filter;
  }
  return params;
};

export const convertNewsletterToFormValues = (data: any) => {
  const values = { ...data };
  if (values.filterJson) {
    values.recipientFilter = JSON.parse(values.filterJson);
    delete values.filterJson;
  }
  return values;
};
